import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { LEVEL_CONFIG } from "../constants/packagingDetail.constants";
import {
  ApiPackaging,
  ApiPackagingItem,
  PackagingDetailViewModel,
  PackagingLevelViewModel,
  PackagingMaterialRecord,
  PackagingUnitFieldKey,
  PackagingUnitSnapshot
} from "../types/packagingDetail.types";


export const mapApiPackagingItemToRecord = (item: ApiPackagingItem): PackagingMaterialRecord => ({
  id: item._id,
  name: item.packageName ?? "",
  material: item.packageMaterial ?? "",
  weight: item.materialWeight === "" || item.materialWeight == null ? "" : Number(item.materialWeight),
  weightUnit: item.weightUnit || String(MEASUREMENT_TYPES.WEIGHT_KG),
  cost: item.materialCost === "" || item.materialCost == null ? "" : Number(item.materialCost)
});

/** `packageMaterial`/`weightUnit` are numeric on write despite being typed `string` on read. */
export const mapRecordToApiMaterialPayload = (
  record: Pick<PackagingMaterialRecord, "name" | "material" | "weight" | "weightUnit" | "cost">
) => ({
  packageName: record.name,
  packageMaterial: Number(record.material || 0),
  materialWeight: record.weight === "" ? 0 : Number(record.weight),
  materialCost: record.cost === "" ? 0 : Number(record.cost),
  contentWeight: 0,
  totalWeightWithPackaging: 0,
  weightUnit: Number(record.weightUnit || MEASUREMENT_TYPES.WEIGHT_KG)
});

const EMPTY_UNIT_SNAPSHOT: PackagingUnitSnapshot = {
  unitSize: 0,
  unitSizeWeightUnit: MEASUREMENT_TYPES.WEIGHT_KG,
  numberOfBaseUnits: 0,
  numberOfPacks: 0,
  numberOfCases: 0,
  palletWidth: 0,
  palletLength: 0
};

const UNIT_VALUE_BY_FIELD: Record<PackagingUnitFieldKey, keyof PackagingUnitSnapshot> = {
  unitSize: "unitSize",
  numberOfBaseUnits: "numberOfBaseUnits",
  numberOfPacks: "numberOfPacks",
  numberOfCases: "numberOfCases"
};

/** Builds the body `useUpdatePackagingDetails` expects — the API replaces the full snapshot
 * on every save, so always resend all 6 numeric fields, not just the one that changed. */
export const buildPackagingUnitPayload = (
  fieldKey: PackagingUnitFieldKey,
  snapshot: PackagingUnitSnapshot,
  patch: Partial<PackagingUnitSnapshot>
) => {
  const merged = { ...snapshot, ...patch };
  return {
    packagingType: fieldKey,
    unitSize: merged.unitSize,
    unitSizeWeightUnit: merged.unitSizeWeightUnit,
    numberOfBaseUnits: merged.numberOfBaseUnits,
    numberOfPacks: merged.numberOfPacks,
    numberOfCases: merged.numberOfCases,
    palletWidth: merged.palletWidth,
    palletLength: merged.palletLength
  };
};

export const mapPackagingDetail = (detail: ApiPackaging | undefined): PackagingDetailViewModel => {
  if (!detail) {
    return { packagingName: "", levels: [], unitSnapshot: EMPTY_UNIT_SNAPSHOT };
  }

  const unitSnapshot: PackagingUnitSnapshot = {
    unitSize: detail.unitSize ?? 0,
    unitSizeWeightUnit: detail.unitSizeWeightUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
    numberOfBaseUnits: detail.numberOfBaseUnits ?? 0,
    numberOfPacks: detail.numberOfPacks ?? 0,
    numberOfCases: detail.numberOfCases ?? 0,
    palletWidth: detail.palletWidth ?? 0,
    palletLength: detail.palletLength ?? 0
  };

  const levels: PackagingLevelViewModel[] = LEVEL_CONFIG.map(config => ({
    key: config.key,
    apiType: config.apiType,
    label: config.label,
    unitFieldKey: config.unitFieldKey,
    unitValue: unitSnapshot[UNIT_VALUE_BY_FIELD[config.unitFieldKey]],
    unitSizeWeightUnit: unitSnapshot.unitSizeWeightUnit,
    palletWidth: unitSnapshot.palletWidth,
    palletLength: unitSnapshot.palletLength,
    totalWeight: detail[config.totalWeightKey] ?? 0,
    materials: (detail[config.key] ?? []).map(mapApiPackagingItemToRecord)
  }));

  return { packagingName: detail.packagingName ?? "", levels, unitSnapshot };
};
